import React, { useEffect, useState } from 'react';

import { useInView } from 'react-intersection-observer';
import { makeStyles, useTheme } from '@material-ui/core/styles';
import Typography from '@material-ui/core/Typography';
import CircularProgress from '@material-ui/core/CircularProgress';
import { Button } from '../Common/Components';
import { DataPass } from '../../types/dataPassport';
import LogoRow from '../Common/LogoRow';
import { getStatistics, recordMerchantEvent } from '../../services/c2dcrm';
import { useAuth } from '../../hooks/useAuth';
import { EventComponent, EventSlug } from '../../constants/events';
import { Grid } from '@material-ui/core';
import { ScanStatistics } from '../../types/crm';
import DataCard from './DataCard';
import { DateTime } from 'luxon';

const useStyles = makeStyles((theme) => ({
  card: {
    position: 'relative',
    display: 'grid',
    gridGap: '16px',
    padding: '32px',
    minWidth: '248px',
    boxShadow: '0px 5px 26px rgba(0, 0, 0, 0.06)',
    backgroundColor: '#FFFFFF',
    [theme.breakpoints.up('sm')]: {
      width: '248px',
    },
    [theme.breakpoints.up('md')]: {
      width: '286px',
    },
    '& a': {
      fontWeight: 700,
      textDecoration: 'none',

      '& svg': {
        marginRight: '8px',
      },
    },
  },
  row: {
    display: 'flex',
    flexDirection: 'row',
    justifyContent: 'space-between',
  },
  logo: {
    width: '50%',
    display: 'grid',
    gridGap: '8px',

    '& img': {
      height: '48px',
      width: '48px',
    },
  },
  logoTitle: {
    fontSize: '10px',
  },
  perksText: {
    textDecoration: 'none',
    marginLeft: '8px',
  },
  loading: {
    position: 'absolute',
    width: '100%',
    height: '100%',

    display: 'grid',
    placeItems: 'center',
    backgroundColor: '#FFFFFF',
    opacity: 0.5,
    borderRadius: '16px',
  },
  clickable: {
    cursor: 'pointer',
  },
  grid: {

  },
  topRight: {
    alignItems: 'right',
    verticalAlign: 'top',
  }
}));

type PropsType = {
  dataPass: DataPass;
  activateButtonText: string;
  onActivateButtonClick: (dataPass: DataPass) => void;
  onDataPassDetailsClick: (dataPass: DataPass) => void;
  loading?: boolean;
  merchant?: boolean;
};

type StateStats = {
  stats?: ScanStatistics;
}

type ActivationStatus = {
  ready: boolean;
  startDate: DateTime;
}

const MerchantDataPassCard: React.FC<PropsType> = ({
  dataPass,
  activateButtonText,
  onActivateButtonClick,
  loading,
  merchant,
}) => {
  const theme = useTheme();
  const classes = useStyles(theme);
  const { token } = useAuth();
  const [s, setS] = useState<StateStats>({ stats: undefined });
  const [res, setRes] = useState<ActivationStatus>({ ready: false, startDate: DateTime.now() });

  const { ref, inView } = useInView({
    threshold: 0,
  });

  const createActivationStatus = () => {
    if (dataPass.start_date) {
      const startDate = DateTime.fromISO(dataPass.start_date);
      if (startDate >= DateTime.now()) {
        setRes({ ready: false, startDate: startDate });
      }
      else if (startDate < DateTime.now()) {
        setRes({ ready: true, startDate: startDate });
      }
      else {
        setRes({ ready: false, startDate: DateTime.now() });
      }
    }
    else {
      setRes({ ready: false, startDate: DateTime.now() });
    }
  }
  
  useEffect(() => {
    if (inView) {
      recordMerchantEvent(token, EventComponent.DATA_PASS, dataPass.id, EventSlug.PASS_IMPRESSION);
    }

    const fetchStats = async () => {
      if (!s.stats && merchant) {
        try {
          const response = await getStatistics(dataPass.id, token)
          if (response) setS({ stats: response.data });
        } catch (error) {
          setS({ stats: undefined });
        }
      }
    }

    fetchStats();
    createActivationStatus();
  }, []);

  return (
    <div className={classes.card} key={dataPass.id}>
      {loading && (
        <div className={classes.loading}>
          <CircularProgress />
        </div>
      )}

      <div>
        <Grid
          container
          alignItems="center"
          justifyContent="center"
          spacing={1}
          className={classes.grid}
        >
          <Grid item xs={12}>
            <Typography variant="h3">{dataPass.title}</Typography>
            <Typography variant="body2">
              <b>Data Pass</b>
            </Typography>
          </Grid>

          <Grid item>
            <Typography ref={ref}>{dataPass.description_for_customers}</Typography>
          </Grid>
        </Grid>
      </div>

      {merchant ? <Typography variant="subtitle1" color="primary" align="center">
        {new Intl.NumberFormat('en-US', {
          style: 'currency',
          currency: dataPass.currency_code,
        }).format(dataPass.price)}
      </Typography> : <></> }

      <LogoRow includeVerifier={false} dataPass={dataPass} />

      <div>
        <Grid
          container
          alignItems="center"
          justifyContent="center"
          direction="column"
          spacing={1}
        >
         
          {res.ready && merchant && <Grid item xs={12} style={{ marginBottom: '20px' }}>
            <Button className="wide" onClick={() => onActivateButtonClick(dataPass)}>
              {activateButtonText}
            </Button>
          </Grid>
          }  
          
          {!res.ready && <Grid item style={{ textAlign: 'center', alignItems: 'center' }}>
              <Typography variant="h3">Coming Soon!<br />{res.startDate.toString().substring(0, 10)} </Typography>
              </Grid>
          }
          {res.ready &&  <Grid item style={{ textAlign: 'center', alignItems: 'center'}}>
            <Typography variant="h3">Transaction Details</Typography>
            {s.stats ? <DataCard data={s.stats} /> : <CircularProgress />}
            </Grid>
          }
        </Grid>
      </div>
    </div>
  );
};

export default MerchantDataPassCard;
